/**
 * AdminAreasPage — calm editorial overview of areas.
 * Mirrors AdminPlacesPage patterns.
 */
import { useNavigate } from "react-router-dom";
import { Plus, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeader } from "@/components/admin/PageHeader";
import { StudioSurface } from "@/components/admin/StudioSurface";
import { EmptyBlock } from "@/components/admin/EmptyBlock";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAreasList, type AdminAreaRow } from "@/hooks/useAdminAreas";
import { cn } from "@/lib/utils";

const TYPE_LABELS: Record<string, string> = {
  bygd: "Bygd",
  destination: "Destination",
  landscape_cluster: "Landskapskluster",
  region: "Region",
};

function AreaCard({ area }: { area: AdminAreaRow }) {
  const navigate = useNavigate();
  const updated = new Date(area.updated_at).toLocaleDateString("sv-SE");

  return (
    <button
      type="button"
      onClick={() => navigate(`/app/admin/areas/${area.id}/edit`)}
      className="w-full text-left flex gap-4 items-start px-4 py-4 border-b last:border-b-0 hover:bg-muted/30 transition-colors"
    >
      {/* Hero thumbnail */}
      <div className="w-16 h-16 rounded-lg bg-muted shrink-0 overflow-hidden">
        {area.hero_url ? (
          <img
            src={area.hero_url}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-5 h-5 text-muted-foreground/40" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-foreground truncate">
            {area.name}
          </span>
          <Badge
            variant="secondary"
            className="text-[10px] py-0 px-1.5 shrink-0"
          >
            {TYPE_LABELS[area.area_type] || area.area_type}
          </Badge>
          <Badge
            variant={area.status === "public" ? "default" : "secondary"}
            className={cn(
              "text-[10px] py-0 px-1.5 shrink-0",
              area.status === "public"
                ? "bg-primary/10 text-primary border-primary/20"
                : ""
            )}
          >
            {area.status === "public" ? "Publicerad" : "Utkast"}
          </Badge>
        </div>

        <p className="text-xs text-muted-foreground mt-0.5 truncate">
          {area.description_short || "Ingen beskrivning ännu"}
        </p>

        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground/70">
          <span>{area.linked_places_count || 0} platser</span>
          <span>Uppdaterad {updated}</span>
        </div>
      </div>
    </button>
  );
}

export default function AdminAreasPage() {
  const navigate = useNavigate();
  const { data: areas = [], isLoading } = useAdminAreasList();

  return (
    <AdminLayout title="Områden">
      <PageHeader
        title="Områden"
        description={`${areas.length} område${areas.length !== 1 ? "n" : ""}`}
        actions={
          <Button
            size="sm"
            onClick={() => navigate("/app/admin/areas/new")}
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden md:inline">Nytt område</span>
            <span className="md:hidden">Nytt</span>
          </Button>
        }
      />

      <div className="px-4 lg:px-8 pb-8">
        <StudioSurface className="p-0 overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : areas.length === 0 ? (
            <EmptyBlock
              title="Inga områden ännu"
              description="Skapa ett område för att rama in platser i ett redaktionellt landskap."
              action={
                <Button onClick={() => navigate("/app/admin/areas/new")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Skapa område
                </Button>
              }
            />
          ) : (
            <div>
              {areas.map((area) => (
                <AreaCard key={area.id} area={area} />
              ))}
            </div>
          )}
        </StudioSurface>
      </div>
    </AdminLayout>
  );
}
